import { globalColumnDefinition } from '../../../common/Grid/GridColumn';
import { formatDate, getDateFromArray } from '../../../utils/dateUtil';
import moment from 'moment-timezone';
import { requiredFields } from './myHolidaysConstants';

export const getColumnDefs = (isEditable: boolean, deleteHoliday: (id: string) => void) => [
  {
    headerName: 'Action',
    cellRenderer: 'DeleteButton',
    cellClass: 'align-items-center d-flex justify-content-center',
    cellRendererProps: { deleteHoliday },
    hide: !isEditable,
  },
  Object.assign({}, globalColumnDefinition.year, {
    editable: isEditable,
    cellRenderer: isEditable ? 'EditableFieldRenderer' : null,
  }),
  Object.assign({}, globalColumnDefinition.country, {
    editable: isEditable,
    cellRenderer: isEditable ? 'EditableFieldRenderer' : null,
  }),
  Object.assign({}, globalColumnDefinition.countryShortCode, {
    editable: isEditable,
    cellRenderer: isEditable ? 'EditableFieldRenderer' : null,
  }),
  Object.assign({}, globalColumnDefinition.date, {
    field: 'holidayDate',
    editable: isEditable,
    cellRenderer: isEditable ? 'EditableFieldRenderer' : null,
    cellEditor: isEditable ? 'HolidayDateCellEditor' : undefined,
    cellEditorPopup: true,
  }),
  Object.assign({}, globalColumnDefinition.description, {
    field: 'holidayName',
    editable: isEditable,
    cellRenderer: isEditable ? 'EditableFieldRenderer' : null,
  }),
  Object.assign({}, globalColumnDefinition.modifiedBy, { field: 'updatedBy' }),
  Object.assign({}, globalColumnDefinition.lastUpdate, { field: 'updatedDate' }),
];

export const formatHolidayData = (data: any[]) =>
  data.map((i) => {
    const holidayDate = formatDate(getDateFromArray(i?.holidayDate));
    const updatedDate = formatDate(getDateFromArray(i?.updatedDate));
    return { ...i, holidayDate, updatedDate };
  });

export const transformCsvMapToHolidayList = (
  map: Record<string, string[]>,
):
  | Array<{
      year: string;
      country: string;
      countryShortCode: string;
      holidayDate: string;
      holidayName: string;
      calendarType: string;
    }>
  | string => {
  const headerMapping: Record<string, string> = {
    Country: 'country',
    'Country Short Code': 'countryShortCode',
    'Calender Type': 'calendarType',
    Year: 'year',
    Date: 'holidayDate',
    Description: 'holidayName',
  };

  const requiredHeaders = Object.keys(headerMapping);

  const headers = Object.keys(map);
  if (headers.length === 0) {
    return 'CSV file is empty or has no headers';
  }

  const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
  if (missingHeaders.length > 0) {
    return `Missing required headers in CSV file: ${missingHeaders.join(', ')}. Required headers are: ${requiredHeaders.join(', ')}`;
  }

  const rowCount = map[headers[0]]?.length || 0;
  const result: Array<{
    year: string;
    country: string;
    countryShortCode: string;
    holidayDate: string;
    holidayName: string;
    calendarType: string;
  }> = [];

  for (let i = 0; i < rowCount; i++) {
    const row: any = {
      year: '',
      country: '',
      countryShortCode: '',
      holidayDate: '',
      holidayName: '',
      calendarType: '',
    };

    headers.forEach((csvHeader) => {
      const fieldName = headerMapping[csvHeader] || csvHeader.toLowerCase().replace(/\s+/g, '');
      if (fieldName in row) {
        let value = map[csvHeader]?.[i] || '';

        if (fieldName === 'holidayDate' && value) {
          const parsedDate = moment(
            value,
            ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'M-D-YYYY', 'MM-DD-YYYY'],
            true,
          );
          if (parsedDate.isValid()) {
            value = parsedDate.format('YYYY-MM-DD');
          } else {
            const defaultParsed = moment(value);
            if (defaultParsed.isValid()) {
              value = defaultParsed.format('YYYY-MM-DD');
            }
          }
        }

        row[fieldName] = value;
      }
    });

    result.push(row);
  }

  return result;
};

export const sampleHolidaysList = () => {
  return {
    data: [
      {
        year: '2026',
        country: 'India',
        countryShortCode: 'IND',
        holidayDate: '01-26-2026',
        holidayName: 'Republic Day',
        calendarType: '',
      },
    ],
    headers: [
      { label: 'Country', key: 'country' },
      { label: 'Country Short Code', key: 'countryShortCode' },
      { label: 'Calender Type', key: 'calendarType' },
      { label: 'Year', key: 'year' },
      { label: 'Date', key: 'holidayDate' },
      { label: 'Description', key: 'holidayName' },
    ],
    filename: 'Sample_Holidays_List',
    className: 'f-12',
    buttonTextContent: 'Sample Holidays List CSV file',
  };
};

export const parseHolidayYear = (year: any): number | null => {
  const yearValue = String(year || '').trim();
  if (!/^\d{4}$/.test(yearValue)) {
    return null;
  }

  const parsedYear = Number(yearValue);
  return Number.isNaN(parsedYear) ? null : parsedYear;
};

export const getYearDateBounds = (year: any): { minDate: Date; maxDate: Date } | null => {
  const parsedYear = parseHolidayYear(year);
  if (!parsedYear) {
    return null;
  }

  return {
    minDate: moment(`${parsedYear}-01-01`, 'YYYY-MM-DD').toDate(),
    maxDate: moment(`${parsedYear}-12-31`, 'YYYY-MM-DD').toDate(),
  };
};

export const isHolidayDateInYear = (holidayDate: any, year: any): boolean => {
  const parsedYear = parseHolidayYear(year);
  const parsedDate = moment(holidayDate);

  return !!parsedYear && parsedDate.isValid() && parsedDate.year() === parsedYear;
};

export const validateRow = (row: any): Set<string> => {
  const emptyFields = new Set<string>();
  requiredFields.forEach((field) => {
    if (!row[field] || (typeof row[field] === 'string' && row[field].trim() === '')) {
      emptyFields.add(field);
    }
  });

  const hasYear = !!row?.year && (typeof row.year !== 'string' || row.year.trim() !== '');
  const parsedYear = parseHolidayYear(row?.year);

  if (hasYear && !parsedYear) {
    emptyFields.add('year');
  }

  if (row?.holidayDate && parsedYear && !isHolidayDateInYear(row.holidayDate, row.year)) {
    emptyFields.add('holidayDate');
  }

  return emptyFields;
};
